import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { collectionsPreviewInvoice, collectionsSendInvoice, collectionsAdvanceInvoice, listDunningLogsForInvoice, getCollectionsSettings } from '../api';

export default function CollectionsTab({ invoice, authAxios, refreshInvoice, showToast }) {
  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const data = await collectionsPreviewInvoice(authAxios, invoice.id);
      setPreviewData(data);
    } catch (e) {
      showToast('Failed to load preview', 'danger');
    } finally {
      setPreviewLoading(false);
    }
  };
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!retryAfter) return;
    const tick = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tick);
  }, [retryAfter]);

  const inCooldown = retryAfter && retryAfter > now;

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await listDunningLogsForInvoice(authAxios, invoice.id);
      setLogs(data);
    } catch (e) {
      console.error('Failed to fetch dunning logs', e);
    } finally {
      setLogsLoading(false);
    }
  };
  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await getCollectionsSettings(authAxios);
      setSettings(data);
    } catch (e) {
      console.error('Failed to load collections settings', e);
      showToast('Failed to load collections settings', 'warning');
    } finally {
      setSettingsLoading(false);
    }
  };
  useEffect(() => { fetchLogs(); fetchSettings(); }, [invoice.id]);

  const collectionsEnabled = settings ? Boolean(settings.collections_enabled) : true;

  const computeStageInfo = () => {
    if (!settings || !invoice?.due_date) return null;
    const due = new Date(invoice.due_date + 'T00:00:00');
    const today = new Date();
    const msPerDay = 86400000;
    const daysOverdue = Math.max(0, Math.floor((today - due) / msPerDay));
    const grace = settings.collections_grace_days || 0;
    const schedule = (settings.collections_schedule || []).map(x => parseInt(x,10)).filter(x=>!isNaN(x)).sort((a,b)=>a-b);
    const currentStage = invoice.dunning_stage || 0;
    if (daysOverdue <= grace) {
      return { phase: 'grace', message: `Grace period active: ${grace - daysOverdue} day(s) remaining`, daysOverdue, nextOffset: null, countdown: grace - daysOverdue, dueNow: false };
    }
    const daysSinceGrace = daysOverdue - grace;
    const nextOffset = schedule[currentStage];
    if (nextOffset == null) {
      return { phase: 'completed', message: 'All scheduled stages completed.', daysOverdue, nextOffset: null, countdown: null, dueNow: false };
    }
    const countdown = nextOffset - daysSinceGrace;
    const dueNow = countdown <= 0;
    let message;
    if (dueNow) message = `Next stage (${currentStage+1}) eligible now (offset ${nextOffset}d since grace).`;
    else message = `Next stage (${currentStage+1}) in ${countdown} day(s) (offset ${nextOffset}d).`;
    return { phase: 'collections', message, daysOverdue, nextOffset, countdown, dueNow };
  };
  const stageInfo = computeStageInfo();

  const computeRateLimit = () => {
    if (!settings || !invoice.last_dunning_sent) return null;
    const intervalHours = settings.collections_min_interval_hours || 12;
    const last = new Date(invoice.last_dunning_sent);
    const now = new Date();
    const diffMs = now - last;
    const remainingMs = intervalHours*3600000 - diffMs;
    if (remainingMs <= 0) return { remainingHours: 0, intervalHours };
    return { remainingHours: +(remainingMs/3600000).toFixed(2), intervalHours };
  };
  const rateInfo = computeRateLimit();

  const sortedLogs = [...logs].sort((a,b) => {
    let av, bv;
    if (sortKey === 'timestamp') { av = new Date(a.timestamp).getTime(); bv = new Date(b.timestamp).getTime(); }
    else if (sortKey === 'action') { av = (a.action||'').toLowerCase(); bv = (b.action||'').toLowerCase(); }
    else if (sortKey === 'stage') { av = a.dunning_stage||0; bv = b.dunning_stage||0; }
    if (typeof av === 'string') return sortDir==='asc'? av.localeCompare(bv): bv.localeCompare(av);
    return sortDir==='asc'? av - bv : bv - av;
  });
  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageSlice = sortedLogs.slice((currentPage-1)*pageSize, currentPage*pageSize);

  const exportCsv = () => {
    const headers = ['timestamp','action','dunning_stage','rate_limited','subject'];
    const rows = sortedLogs.map(l => headers.map(h => {
      let val = l[h];
      if (h==='timestamp') val = l.timestamp ? new Date(l.timestamp).toISOString() : '';
      if (h==='rate_limited') val = l.rate_limited ? 'true':'false';
      return (String(val||'')).replace(/"/g,'""');
    }));
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dunning_logs_invoice_${invoice.invoice_number || invoice.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendNow = async () => {
    try {
      await collectionsSendInvoice(authAxios, invoice.id);
      showToast('Dunning email sent', 'success');
      setRetryAfter(null);
      await refreshInvoice();
      setPreviewData(null);
      fetchLogs();
    } catch (e) {
      const status = e.response?.status;
      const body = e.response?.data;
      if (status === 429) {
        const next = body?.retry_after ? new Date(body.retry_after) : null;
        if (next && !isNaN(next.getTime())) setRetryAfter(next);
        showToast(body?.detail || 'Rate limit active. Try later.', 'warning');
      } else {
        showToast(body?.detail || 'Failed to send dunning email', 'danger');
      }
    }
  };

  const advanceStage = async (sendImmediately=false) => {
    try {
      const resp = await collectionsAdvanceInvoice(authAxios, invoice.id, sendImmediately);
      showToast(`Stage advanced to ${resp.advanced_to}`, 'success');
      await refreshInvoice();
      setPreviewData(null);
      fetchLogs();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to advance stage', 'danger');
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${invoice.is_in_collections ? 'bg-rose-50 text-rose-800 ring-rose-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'}`}>
                {invoice.is_in_collections ? 'In Collections' : 'Not in Collections'}
              </span>
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset bg-gray-50 text-gray-700 ring-gray-200">
                Stage {invoice.dunning_stage || 0}
              </span>
              <span className="text-gray-600">
                Last sent: <span className="font-medium text-gray-900">{invoice.last_dunning_sent ? new Date(invoice.last_dunning_sent).toLocaleString() : 'Never'}</span>
              </span>
            </div>

            {settingsLoading && <p className="text-sm text-gray-500">Loading collections settings…</p>}
            {settings && !collectionsEnabled && (
              <p className="text-sm text-rose-700">Collections are not enabled for this firm.</p>
            )}

            {stageInfo && (
              <p className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Next stage:</span>{' '}
                <span className={stageInfo.dueNow ? 'text-rose-700 font-semibold' : 'text-gray-700'}>{stageInfo.message}</span>
              </p>
            )}
            {rateInfo && (
              <p className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Rate limit:</span>{' '}
                {rateInfo.remainingHours > 0
                  ? `${rateInfo.remainingHours}h remaining (interval ${rateInfo.intervalHours}h)`
                  : `Window open (interval ${rateInfo.intervalHours}h)`}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={loadPreview}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
              disabled={previewLoading}
            >
              {previewLoading ? 'Loading…' : 'Preview'}
            </button>
            <button
              onClick={sendNow}
              disabled={(settings ? !collectionsEnabled : false) || inCooldown}
              title={
                settings && !collectionsEnabled
                  ? 'Enable collections in firm settings first.'
                  : inCooldown
                    ? `Rate-limited. Next attempt allowed at ${retryAfter.toLocaleString()}.`
                    : ''
              }
              className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inCooldown ? `Send (wait until ${retryAfter.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : 'Send'}
            </button>
            <button
              onClick={() => advanceStage(false)}
              disabled={settings ? !collectionsEnabled : false}
              title={settings && !collectionsEnabled ? 'Enable collections in firm settings first.' : ''}
              className="inline-flex items-center justify-center rounded-md bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900 ring-1 ring-inset ring-amber-200 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Advance
            </button>
            <button
              onClick={() => advanceStage(true)}
              disabled={settings ? !collectionsEnabled : false}
              title={settings && !collectionsEnabled ? 'Enable collections in firm settings first.' : ''}
              className="inline-flex items-center justify-center rounded-md bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-900 ring-1 ring-inset ring-rose-200 hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Advance + Send
            </button>
          </div>
        </div>
      </div>

      {previewLoading && <div className="text-gray-500 text-sm">Loading preview…</div>}
      {previewData && previewData.can_send && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Preview (Stage {previewData.dunning_stage})</h4>
          </div>
          <p className="mt-2 text-sm text-gray-700"><span className="font-semibold text-gray-900">Subject:</span> {previewData.subject}</p>
          <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-800" style={{whiteSpace:'pre-line'}}>{previewData.body}</div>
        </div>
      )}
      {previewData && !previewData.can_send && (
        <div className="text-sm text-rose-700">Cannot send: {previewData.reason}</div>
      )}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h4 className="text-sm font-semibold text-gray-900">Recent Dunning Communications</h4>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-2">Sort
              <select value={sortKey} onChange={e=>{setSortKey(e.target.value); setPage(1);}} className="rounded-md border border-gray-300 bg-white px-2 py-1">
                <option value="timestamp">Time</option>
                <option value="action">Action</option>
                <option value="stage">Stage</option>
              </select>
            </label>
            <button onClick={()=>setSortDir(d=> d==='asc'?'desc':'asc')} className="rounded-md bg-white px-3 py-1 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">{sortDir==='asc'?'Asc':'Desc'}</button>
            <label className="flex items-center gap-2">Page size
              <select value={pageSize} onChange={e=>{setPageSize(parseInt(e.target.value,10)); setPage(1);}} className="rounded-md border border-gray-300 bg-white px-2 py-1">
                {[5,10,20,50].map(n=> <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={fetchLogs} className="rounded-md bg-white px-3 py-1 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Refresh</button>
            <button onClick={exportCsv} className="rounded-md bg-emerald-50 px-3 py-1 font-semibold text-emerald-900 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">Export CSV</button>
          </div>
        </div>
        {logsLoading && <div className="text-sm text-gray-500">Loading logs…</div>}
        {!logsLoading && logs.length === 0 && <div className="text-sm text-gray-500">No communications yet.</div>}
        {!logsLoading && logs.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Stage</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rate Limited</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subject</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {pageSlice.map(l => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-gray-700">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-800">{l.action || l.log_type}</td>
                      <td className="px-4 py-2 text-gray-800">{l.dunning_stage || '-'}</td>
                      <td className="px-4 py-2 text-gray-800">{l.rate_limited ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2 truncate max-w-xs text-gray-800" title={l.subject}>{l.subject || '(no subject)'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!logsLoading && logs.length > 0 && (
          <div className="flex items-center justify-between mt-3 text-sm text-gray-700">
            <div>Page {currentPage} / {totalPages} ({logs.length} total)</div>
            <div className="flex items-center gap-2">
              <button disabled={currentPage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="rounded-md bg-white px-3 py-1 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40">Prev</button>
              <button disabled={currentPage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} className="rounded-md bg-white px-3 py-1 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
